import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { treks } from '../data/trekData';
import { MapPin, Clock, TrendingUp, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PriceAnimation from '@/components/ui/price-animation';

const Treks = () => {
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  const filteredTreks = difficultyFilter === 'all'
    ? treks
    : treks.filter(trek => trek.difficulty.toLowerCase().includes(difficultyFilter.toLowerCase()));

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-80 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-slate-900/70 z-10" />
          <img
            src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=1920&q=80"
            alt="Mountain treks"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-20 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Our Treks
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Choose your next adventure from our carefully curated collection
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-lg font-semibold text-slate-900">
              Showing {filteredTreks.length} {filteredTreks.length === 1 ? 'trek' : 'treks'}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Filter by difficulty:</span>
              <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Difficulties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Difficulties</SelectItem>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="difficult">Difficult</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Treks Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTreks.map((trek) => (
              <Card
                key={trek.id}
                className="overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={trek.image}
                    alt={trek.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-orange-600 hover:bg-orange-700 text-white">
                      {trek.difficulty}
                    </Badge>
                  </div>
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="bg-white/90 text-slate-900">
                      {trek.duration}
                    </Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl group-hover:text-orange-600 transition-colors">
                    {trek.name}
                  </CardTitle>
                  <CardDescription className="flex items-center text-gray-600">
                    <MapPin className="h-4 w-4 mr-1" />
                    {trek.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4 line-clamp-2">{trek.shortDescription}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="h-4 w-4 mr-2 text-orange-600" />
                      <span>{trek.duration}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <TrendingUp className="h-4 w-4 mr-2 text-orange-600" />
                      <span>Max Altitude: {trek.altitude}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <div className="text-sm text-gray-500 mb-1">Highlights:</div>
                    <ul className="space-y-1">
                      {trek.highlights.slice(0, 2).map((highlight, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-start">
                          <span className="text-orange-600 mr-2">•</span>
                          <span className="line-clamp-1">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="flex items-center justify-between bg-slate-50 pt-6">
                  <div>
                    <div className="text-sm text-gray-500 pb-1.5">Starting from</div>
                    <div className="max-w-[150px] overflow-hidden text-ellipsis">
                      <PriceAnimation actual={trek.actualPrice} offer={trek.offerPrice} />
                    </div>
                    <div className="text-sm text-gray-500 pt-1.5">per person</div>
                  </div>
                  <Button
                    asChild
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <Link to={`/trek/${trek.id}`}>
                      View Details
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {filteredTreks.length === 0 && (
            <div className="text-center py-16">
              <p className="text-xl text-gray-600">No treks found matching your criteria.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Treks;